import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../UI/Buttons';
import { Title, Label, Text } from '../../UI/Typography';
import { FormGroup, Input } from '../../UI/Inputs';
import { registerUser } from '../../../Services/Api/userService';
import { FaEye, FaLock } from 'react-icons/fa';
import { useAuth } from '../../../Context/AuthContext';

const FormContainer = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  padding: ${({ theme }) => theme.spacing.md};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const SubmitButton = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing.md};
  width: 100%;
  background-color: ${({ theme }) => theme.colors.success};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const StatusMessage = styled.div`
  color: ${props => (props.type === 'success' ? props.theme.colors.success : props.theme.colors.error)};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${props => 
    props.type === 'success' 
      ? `${props.theme.colors.success}15`
      : `${props.theme.colors.error}15`
  };
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const AdminNote = styled.div`
  background-color: ${({ theme }) => `${theme.colors.primary}15`};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

function UserRegisterForm({ onSuccessfulSubmit, onClose, companyData, isAdminSetup = false }) {
  const { user } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState(isAdminSetup ? ['Admin'] : []);
  const [roleInput, setRoleInput] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleRoleInputChange = (e) => {
    setRoleInput(e.target.value);
  };

  const handleRoleInputKeyDown = (e) => {
    if (e.key === 'Enter' && roleInput.trim()) {
      e.preventDefault();
      if (!role.includes(roleInput.trim())) {
        setRole([...role, roleInput.trim()]);
      }
      setRoleInput('');
    }
  };

  const handleRemoveRole = (removeRole) => {
    if (isAdminSetup && removeRole === 'Admin') {
      return; // Don't allow removing Admin role in admin setup
    }
    setRole(role.filter(r => r !== removeRole));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus(null);

    const company_id = companyData?.company_id || user?.company?.company_id || user?.company_id || '';
    const userData = {
      username,
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      role,
      company_id
    };

    try {
      await registerUser(userData);

      setIsLoading(false);
      setSubmitStatus({ 
        type: 'success', 
        message: isAdminSetup 
          ? 'Admin user created successfully! Registration complete.' 
          : 'User registered successfully!' 
      });

      setUsername('');
      setEmail('');
      setFirstName('');
      setLastName('');
      setPassword('');
      setRole(isAdminSetup ? ['Admin'] : []);
      setRoleInput('');

      if (onSuccessfulSubmit) {
        setTimeout(() => {
          onSuccessfulSubmit();
        }, 1500);
      }
    } catch (error) {
      setIsLoading(false);
      setSubmitStatus({ type: 'error', message: error.message || 'Failed to register user.' });
    }
  };

  return (
    <FormContainer>
      <Title style={{ textAlign: 'center', marginBottom: '1rem' }}>
        {isAdminSetup ? '' : 'Register New User'}
      </Title>
      <form onSubmit={handleSubmit}>
        <FormGrid>
          <FormGroup>
            <Label htmlFor="userUsername">Username *</Label>
            <Input
              type="text"
              id="userUsername"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              required
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="userEmail">Email *</Label>
            <Input
              type="email"
              id="userEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@company.com"
              required
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="userFirstName">First Name *</Label>
            <Input
              type="text"
              id="userFirstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              required
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="userLastName">Last Name *</Label>
            <Input
              type="text"
              id="userLastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              required
              disabled={isLoading}
            />
          </FormGroup>
        </FormGrid>

        {!isAdminSetup && (
          <FormGroup>
            <Label htmlFor="userRole">Role(s):</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
              {role.map((r, idx) => (
                <span
                  key={idx}
                  style={{
                    display: 'inline-block',
                    background: r === 'Admin' ? '#e0e7ff' : '#f3f4f6',
                    color: r === 'Admin' ? '#3730a3' : '#374151',
                    borderRadius: 12,
                    padding: '2px 10px',
                    margin: '0 6px 6px 0',
                    fontSize: '0.95em',
                    fontWeight: 500,
                  }}
                >
                  {r}
                  {!(isAdminSetup && r === 'Admin') && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRole(r)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc2626',
                        marginLeft: 4,
                        cursor: 'pointer',
                        fontSize: '1em',
                      }}
                      title="Remove role"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!isAdminSetup && (
                <Input
                  type="text"
                  id="userRole"
                  value={roleInput}
                  onChange={handleRoleInputChange}
                  onKeyDown={handleRoleInputKeyDown}
                  disabled={isLoading}
                  placeholder="Type role and press Enter"
                  style={{ minWidth: 120, flex: 1 }}
                />
              )}
            </div>
          </FormGroup>
        )}

        <FormGroup>
          <Label htmlFor="userPassword">Password *</Label>
          <InputWrapper>
            <Input
              type={isPasswordVisible ? 'text' : 'password'}
              id="userPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter secure password"
              required
              minLength="8"
              disabled={isLoading}
            />
            <PasswordToggleButton
              type="button"
              onClick={togglePasswordVisibility}
              title={isPasswordVisible ? "Hide password" : "Show password"}
            >
              {isPasswordVisible ? <FaLock /> : <FaEye />}
            </PasswordToggleButton>
          </InputWrapper>
        </FormGroup>

        {submitStatus && (
          <StatusMessage type={submitStatus.type}>
            {submitStatus.message}
          </StatusMessage>
        )}

        <SubmitButton type="submit" disabled={isLoading}>
          <Text size="md" weight="semibold" style={{ margin: 0, color: 'inherit' }}>
            {isLoading 
              ? (isAdminSetup ? 'Creating Admin...' : 'Registering...') 
              : (isAdminSetup ? 'Complete Setup' : 'Register User')
            }
          </Text>
        </SubmitButton>
      </form>
    </FormContainer>
  );
}

export default UserRegisterForm;
